import { CalendarCheck, FileCheck2, Layers, Lock, ShowerHead, UserPlus, Users, Wallet } from "lucide-react";
import { BOOKING, IMAGES } from "@/lib/site";
import { Reveal, SectionHeading } from "./Reveal";
import { SiteButton } from "./SiteButton";

const points = [
  {
    icon: Lock,
    title: "One booking, the whole space",
    body: "Your hour, your venue. The sauna, ice bath and deck are yours alone — no strangers, no sharing.",
  },
  {
    icon: Users,
    title: "Come solo or bring the crew",
    body: "The booking holder can fly solo or invite up to five friends — six people in total.",
  },
  {
    icon: UserPlus,
    title: "Everyone sets up their own account",
    body: "Each guest creates their own account and completes their own waiver and health questions.",
  },
  {
    icon: Wallet,
    title: "Guests pay their own way",
    body: "Each invited guest pays their own A$10 when they accept — no chasing mates for cash.",
  },
  {
    icon: FileCheck2,
    title: "Confirmed means complete",
    body: "A guest spot locks in only once their forms and payment are done. Simple, fair, sorted.",
  },
  {
    icon: ShowerHead,
    title: "Individual premium showers & change rooms",
    body: "Top-quality individual shower facilities and a private changing room — arrive, transform, and leave fresh.",
  },
  {
    icon: Layers,
    title: "Two towels, on us",
    body: "Every guest gets a towel for the therapy circuit and a fresh one for the shower after. Just bring yourself.",
  },
];

const PrivateSession = () => {
  return (
    <section data-testid="private-session-section" className="py-24 md:py-32 bg-coal border-y border-line/60">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <Reveal className="lg:col-span-5 order-2 lg:order-1">
          <div className="relative">
            <div className="overflow-hidden border border-line aspect-[4/3] group">
              <img
                src={IMAGES.social}
                alt="Three friends relaxing together in the warm TEMPA sauna"
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
            </div>
            <div className="absolute -bottom-6 -right-4 sm:-right-6 bg-clay border border-line px-6 py-5">
              <p className="font-display text-3xl text-cream">6</p>
              <p className="mt-1 text-xs tracking-[0.2em] uppercase text-stone2">people max</p>
            </div>
          </div>
        </Reveal>

        <div className="lg:col-span-7 order-1 lg:order-2">
          <SectionHeading
            overline="Private sessions"
            title={
              <>
                Booked by one. <em className="italic text-ember">Shared with friends.</em>
              </>
            }
          />
          <Reveal delay={0.05}>
            <p className="mt-6 text-base md:text-lg font-light leading-relaxed text-sand max-w-2xl">
              It's the number one reason people choose TEMPA:{" "}
              <span className="text-cream font-medium">privacy</span>. No strangers on
              the next bench, no queue for the ice bath, no audience while you find
              your breath. For your hour, the whole venue belongs to you — privacy
              for yourself, and privacy for your friends.
            </p>
          </Reveal>
          <div className="mt-12 space-y-8">
            {points.map((point, i) => (
              <Reveal key={point.title} delay={i * 0.08}>
                <div className="flex gap-5" data-testid={`private-point-${i + 1}`}>
                  <div className="shrink-0 flex h-11 w-11 items-center justify-center border border-line bg-ink">
                    <point.icon className="h-5 w-5 text-ember" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-cream">{point.title}</h3>
                    <p className="mt-1 text-sm md:text-base font-light leading-relaxed text-sand">
                      {point.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-12">
            <SiteButton testid="private-session-book-button" href={BOOKING.book}>
              Book a private session
            </SiteButton>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default PrivateSession;
